
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { createSlug, getCategoryName } from "@/utils/categoryUtils";

export interface Category {
  id: string;
  name: string;
  slug: string;
}

export const useCategories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log("Starting to fetch categories...");
      
      // Test basic Supabase connection first
      const { data: connectionTest, error: connectionError } = await supabase
        .from('story_categories')
        .select('category_id')
        .limit(1);
        
      console.log("Connection test result:", { connectionTest, connectionError });
      
      if (connectionError) {
        console.error("Connection error:", connectionError);
        throw new Error(`Database connection failed: ${connectionError.message}`);
      }

      // Get categories from the story_categories table
      const { data: categoryIds, error: categoryIdsError } = await supabase
        .from('story_categories')
        .select('category_id');
        
      console.log("Category IDs fetch result:", { categoryIds, categoryIdsError });
        
      if (categoryIdsError) {
        console.error("Category IDs error:", categoryIdsError);
        throw categoryIdsError;
      }

      // Use a Set to store unique category IDs
      const uniqueCategoryIds = new Set<string>();
      
      // Extract unique category IDs
      if (categoryIds && categoryIds.length > 0) {
        categoryIds.forEach(item => {
          uniqueCategoryIds.add(String(item.category_id));
        });
      }
      
      console.log("Unique category IDs:", Array.from(uniqueCategoryIds));
      
      // For each category ID, fetch or create the proper category with name and slug
      const formattedCategories: Category[] = [];
      const existingIds = new Set();
      
      // Process unique category IDs
      uniqueCategoryIds.forEach(id => {
        // Skip duplicates
        if (existingIds.has(id)) return;
        
        existingIds.add(id);
        
        // Convert ID to a name and slug
        const categoryName = getCategoryName(id);
        const categorySlug = createSlug(categoryName);
        
        formattedCategories.push({
          id,
          name: categoryName,
          slug: categorySlug
        });
      });
      
      // Ensure we have the default categories
      const defaultCategories = [
        { id: "1", name: "Fantasy", slug: "fantasy" },
        { id: "2", name: "Science Fiction", slug: "science-fiction" },
        { id: "3", name: "Romance", slug: "romance" },
        { id: "4", name: "Mystery", slug: "mystery" },
        { id: "5", name: "Horror", slug: "horror" },
        { id: "6", name: "Thriller", slug: "thriller" },
        { id: "7", name: "Historical Fiction", slug: "historical-fiction" },
        { id: "8", name: "Young Adult", slug: "young-adult" },
        { id: "9", name: "Children's", slug: "childrens" },
        { id: "10", name: "Non-fiction", slug: "non-fiction" },
        { id: "11", name: "Fiction", slug: "fiction" },
        { id: "12", name: "Sci-Fi", slug: "scifi" },
        { id: "13", name: "Adventure", slug: "adventure" },
        { id: "14", name: "Drama", slug: "drama" }
      ];
      
      // Add any default categories that aren't already in our list
      for (const defaultCat of defaultCategories) {
        if (!existingIds.has(defaultCat.id)) {
          formattedCategories.push(defaultCat);
          existingIds.add(defaultCat.id);
        }
      }
      
      // Sort categories by name
      formattedCategories.sort((a, b) => a.name.localeCompare(b.name));
      
      console.log("Final formatted categories:", formattedCategories);
      setCategories(formattedCategories);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setError(err instanceof Error ? err : new Error('Failed to fetch categories'));
      
      // Fallback to default categories on error
      const fallbackCategories = [
        { id: "1", name: "Fantasy", slug: "fantasy" },
        { id: "2", name: "Science Fiction", slug: "science-fiction" },
        { id: "3", name: "Romance", slug: "romance" },
        { id: "4", name: "Mystery", slug: "mystery" },
        { id: "5", name: "Horror", slug: "horror" },
        { id: "6", name: "Thriller", slug: "thriller" }
      ];
      
      console.log("Using fallback categories:", fallbackCategories);
      setCategories(fallbackCategories);
      
      toast({
        title: "Unable to load categories from database",
        description: "Using default categories. Please check your connection.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  return {
    categories,
    isLoading,
    error,
    refetch: fetchCategories
  };
};
